import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import LabeledInput from '../LabeledInput';
import { getOptionsIntersection } from '../utils';
import { GroupInputs } from '../GroupInputs';
import { SelectInput } from '../SelectInput';

import * as api from '../../api';
import * as logger from '../../logger';

interface Props {
  value: string;
  updateFieldValue: (value: string) => void;
  saveFieldValue: (value: string) => void;
  statuses: api.PatientSocialHistory.AlcoholUsageStatus[];
  frequencies: api.Previsit.Frequency[];
  quantities: string[];
}

const yesNoSelectValues = [
  {
    display: 'Yes',
    key: 'yes',
    value: 'true',
  },
  { display: 'No', key: 'no', value: 'false' },
];

const labels = {
  amount: 'When you drink alcohol, how many drinks do you typically consume?',
  amountPast:
    'When you used to drink alcohol, how many drinks per day did you typically consume?',
};

export const AlcoholUsage: React.FC<Props> = ({
  value,
  updateFieldValue,
  saveFieldValue,
  statuses,
  frequencies,
  quantities,
}) => {
  const { t } = useTranslation();
  const alcoholUsageInit: Partial<api.PatientSocialHistory.AlcoholUsage> = {};
  const [alcoholUsage, setAlcoholUsage] = useState(alcoholUsageInit);

  useEffect(() => {
    try {
      if (!value) return;
      const parsedValue: api.PatientSocialHistory.AlcoholUsage =
        JSON.parse(value);
      setAlcoholUsage(parsedValue);
    } catch (e) {
      logger.error(e, 'Error parsing alcohol usage value');
    }
  }, [value]);

  const usageSelectFrequencyValues = getOptionsIntersection(
    api.Previsit.Frequency,
    frequencies,
  ).map((freq) => {
    return {
      display: t(api.Previsit.FrequencyMap[freq]),
      key: freq,
      value: freq,
    };
  });

  const statusSelectValues = getOptionsIntersection(
    api.PatientSocialHistory.AlcoholUsageStatus,
    statuses,
  ).map((status) => {
    return {
      display: t(api.Previsit.alcoholUsageStatusMap[status]),
      key: status,
      value: status,
    };
  });

  const quantitiesSelectValues = quantities.map((quantity) => ({
    display: t(quantity),
    key: quantity,
    value: quantity,
  }));

  const alcoholUsageUpdate = (update: {
    [key in keyof api.PatientSocialHistory.AlcoholUsage]?:
      | string
      | number
      | boolean;
  }) => {
    if (
      [
        api.PatientSocialHistory.AlcoholUsageStatus.NeverUsed,
        api.PatientSocialHistory.AlcoholUsageStatus.UnableToObtain,
        api.PatientSocialHistory.AlcoholUsageStatus.PreferNotToAnswer,
      ].some((status) => status === update?.status)
    ) {
      return JSON.stringify({
        ...update,
        concernsAtHome: alcoholUsage.concernsAtHome,
      });
    }
    return JSON.stringify({ ...alcoholUsage, ...update });
  };

  const saveFormField = (value: string) => {
    updateFieldValue(value);
    saveFieldValue(value);
  };

  if (alcoholUsage.status && !statuses.includes(alcoholUsage.status)) {
    const filteredAlcoholUsage = {
      ...alcoholUsage,
      status: undefined,
    };
    saveFormField(JSON.stringify(filteredAlcoholUsage));
    setAlcoholUsage(filteredAlcoholUsage);
  }

  return (
    <GroupInputs>
      <LabeledInput label={t('Status')} htmlFor="alcohol-usage-status">
        <SelectInput
          id="alcohol-usage-status"
          value={alcoholUsage.status || ''}
          onChange={(newValue: string) => {
            saveFormField(alcoholUsageUpdate({ status: newValue }));
          }}
          options={statusSelectValues}
        />
      </LabeledInput>
      {alcoholUsage.status &&
        ![
          api.PatientSocialHistory.AlcoholUsageStatus.NeverUsed,
          api.PatientSocialHistory.AlcoholUsageStatus.UnableToObtain,
          api.PatientSocialHistory.AlcoholUsageStatus.PreferNotToAnswer,
        ].includes(alcoholUsage.status) && (
          <>
            <LabeledInput
              label={t('Frequency')}
              htmlFor="alcohol-usage-frequency"
            >
              <SelectInput
                id="alcohol-usage-frequency"
                value={alcoholUsage.frequency || ''}
                onChange={(newValue: string) => {
                  saveFormField(alcoholUsageUpdate({ frequency: newValue }));
                }}
                options={usageSelectFrequencyValues}
              />
            </LabeledInput>
            <LabeledInput
              label={
                [
                  api.PatientSocialHistory.AlcoholUsageStatus.FormerlyUsed,
                  api.PatientSocialHistory.AlcoholUsageStatus.InRecovery,
                  api.PatientSocialHistory.AlcoholUsageStatus.NotSincePregnant,
                  api.PatientSocialHistory.AlcoholUsageStatus.InEarlyPregnancy,
                ].includes(alcoholUsage.status)
                  ? t(labels.amountPast)
                  : t(labels.amount)
              }
              htmlFor="alcohol-usage-amount"
            >
              <SelectInput
                id="alcohol-usage-amount"
                value={alcoholUsage.quantity ? `${alcoholUsage.quantity}` : ''}
                onChange={(newValue: string) => {
                  saveFormField(alcoholUsageUpdate({ quantity: +newValue }));
                }}
                options={quantitiesSelectValues}
              />
            </LabeledInput>
          </>
        )}
      <LabeledInput
        label={t('Are you concerned about alcohol use at home?')}
        htmlFor="alcohol-usage-concerned"
      >
        <SelectInput
          id="alcohol-usage-concerned"
          value={
            alcoholUsage.concernsAtHome !== undefined
              ? `${alcoholUsage.concernsAtHome}`
              : ''
          }
          onChange={(newValue: string) => {
            saveFormField(
              alcoholUsageUpdate({ concernsAtHome: newValue === 'true' }),
            );
          }}
          options={yesNoSelectValues}
        />
      </LabeledInput>
    </GroupInputs>
  );
};
